import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, CheckCircle2, SkipForward, ArrowDownCircle, ArrowUpCircle,
  Pencil, MessageSquareWarning, X,
} from "lucide-react";

export default function OpsKpiRevisarPropuestas() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [step, setStep] = useState(0);
  const [reviewedCount, setReviewedCount] = useState(0);
  const [totalOriginal, setTotalOriginal] = useState(0);
  const [saving, setSaving] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjust, setAdjust] = useState({ target_value: "", acceptable_min: "", acceptable_max: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/ops/admin/kpis/pending-confirmation");
      setItems(res.data.items || []);
      setTotalOriginal(res.data.count || 0);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const current = items[step];

  useEffect(() => {
    if (!current) return;
    setAdjust({
      target_value: current.target_value ?? "",
      acceptable_min: current.acceptable_min ?? "",
      acceptable_max: current.acceptable_max ?? "",
    });
    setAdjustOpen(false);
  }, [current]);

  const next = () => setStep((s) => (s + 1 >= items.length ? items.length : s + 1));

  const approveAsIs = async () => {
    setSaving(true);
    try {
      await api.put(`/ops/admin/kpis/definitions/${current.id}/approve-formula`, { approved: true });
      setReviewedCount((c) => c + 1);
      toast.success("KPI aprobado");
      next();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al aprobar");
    } finally {
      setSaving(false);
    }
  };

  const adjustAndApprove = async () => {
    setSaving(true);
    try {
      await api.put(`/ops/admin/kpis/definitions/${current.id}/approve-formula`, {
        approved: true,
        target_value: adjust.target_value !== "" ? parseFloat(adjust.target_value) : null,
        acceptable_min: adjust.acceptable_min !== "" ? parseFloat(adjust.acceptable_min) : null,
        acceptable_max: adjust.acceptable_max !== "" ? parseFloat(adjust.acceptable_max) : null,
      });
      setReviewedCount((c) => c + 1);
      toast.success("KPI ajustado y aprobado");
      next();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al aprobar");
    } finally {
      setSaving(false);
    }
  };

  const skip = () => next();

  if (loading) {
    return <div className="p-6 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /> Cargando propuestas…</div>;
  }

  const done = !current || step >= items.length;
  const DirIcon = current?.score_direction === "lower_is_better" ? ArrowDownCircle : ArrowUpCircle;

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-5">
      <button onClick={() => navigate("/ops/admin/kpis/catalogo")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="review-proposals-back">
        <ArrowLeft className="w-4 h-4" /> Volver al catálogo
      </button>

      <div className="flex items-center gap-2">
        <MessageSquareWarning className="w-6 h-6 text-blue-600" />
        <h1 className="text-2xl font-bold text-slate-900">Revisar propuestas de fórmula</h1>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16" data-testid="review-proposals-empty">
          <CheckCircle2 className="w-14 h-14 text-green-400 mx-auto mb-3" />
          <p className="text-lg font-semibold text-slate-700">No hay propuestas pendientes de confirmación.</p>
        </div>
      ) : done ? (
        <div className="text-center py-16" data-testid="review-proposals-finished">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <p className="text-xl font-bold text-slate-800">{reviewedCount} propuestas revisadas</p>
          <p className="text-sm text-slate-400 mt-1">Las que no revisaste quedaron pendientes; puedes volver más tarde.</p>
          <button onClick={() => navigate("/ops/admin/kpis/catalogo")} className="mt-5 px-5 py-2.5 rounded-xl bg-[#1A1A1A] text-white font-semibold text-sm hover:bg-black transition-colors" data-testid="review-proposals-go-catalog">
            Ir al catálogo
          </button>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500" style={{ width: `${(step / totalOriginal) * 100}%` }} />
            </div>
            <span className="text-xs font-semibold text-slate-500" data-testid="review-proposals-progress">{step + 1} de {totalOriginal} revisados</span>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4" data-testid="review-proposals-step">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{current.category_code}</span>
                <h2 className="text-lg font-bold text-slate-900 mt-1.5 flex items-center gap-1.5" data-testid="review-proposals-kpi-name">
                  {current.name}
                  <DirIcon className="w-4 h-4 text-blue-500" title={current.score_direction === "lower_is_better" ? "Menor es mejor" : "Mayor es mejor"} />
                </h2>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 text-sm text-amber-800 flex items-start gap-2" data-testid="review-proposals-notes">
              <MessageSquareWarning className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{current.config_notes || "Sin nota adicional."}</span>
            </div>

            <div>
              <div className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Fórmula sugerida</div>
              <div className="font-mono text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-700">{current.formula_human}</div>
            </div>

            <div className="grid grid-cols-3 gap-2 bg-slate-50 rounded-xl p-3">
              <div>
                <div className="text-[10px] font-semibold text-slate-400 uppercase">Meta sugerida</div>
                <div className="text-sm font-semibold text-slate-700" data-testid="review-proposals-target">{current.target_value ?? "—"}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold text-slate-400 uppercase">Rango aceptable</div>
                <div className="text-sm font-semibold text-slate-700">{current.acceptable_min ?? "—"} – {current.acceptable_max ?? "—"}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold text-slate-400 uppercase">Dirección</div>
                <div className="text-sm font-semibold text-slate-700">{current.score_direction === "lower_is_better" ? "Menor es mejor" : "Mayor es mejor"}</div>
              </div>
            </div>

            {adjustOpen && (
              <div className="border border-slate-200 rounded-xl p-3 space-y-2 bg-slate-50" data-testid="review-proposals-adjust-panel">
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-500 mb-1">Meta</label>
                    <input type="number" value={adjust.target_value} onChange={(e) => setAdjust((p) => ({ ...p, target_value: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" data-testid="review-proposals-adjust-target" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-500 mb-1">Mínimo aceptable</label>
                    <input type="number" value={adjust.acceptable_min} onChange={(e) => setAdjust((p) => ({ ...p, acceptable_min: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" data-testid="review-proposals-adjust-min" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-500 mb-1">Máximo aceptable</label>
                    <input type="number" value={adjust.acceptable_max} onChange={(e) => setAdjust((p) => ({ ...p, acceptable_max: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" data-testid="review-proposals-adjust-max" />
                  </div>
                </div>
                <button onClick={adjustAndApprove} disabled={saving} className="w-full py-2 rounded-lg bg-blue-600 font-semibold text-sm text-white hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="review-proposals-confirm-adjust">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Confirmar ajuste y aprobar
                </button>
              </div>
            )}

            <div className="flex gap-2 pt-2 border-t border-slate-100">
              <button onClick={skip} className="flex-1 inline-flex items-center justify-center gap-1.5 py-3 rounded-xl border border-slate-200 font-semibold text-slate-600 hover:bg-slate-50 transition-colors" data-testid="review-proposals-skip">
                <SkipForward className="w-4 h-4" /> Saltar
              </button>
              <button
                onClick={() => setAdjustOpen((p) => !p)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 py-3 rounded-xl border border-blue-200 text-blue-700 font-semibold hover:bg-blue-50 transition-colors"
                data-testid="review-proposals-adjust-btn"
              >
                {adjustOpen ? <X className="w-4 h-4" /> : <Pencil className="w-4 h-4" />} Ajustar y aprobar
              </button>
              <button onClick={approveAsIs} disabled={saving} className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-[#1A1A1A] text-white font-semibold hover:bg-black transition-colors disabled:opacity-50" data-testid="review-proposals-approve-as-is">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Aprobar tal cual
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
